import { useCallback, useEffect, useState } from 'react';
import { configureAuth, currentUserEmail, endSession, startSignIn } from './auth';
import { configureApi, getHealth, isNotStaff, isUnavailable } from './api';
import { message } from './lib/async';
import { NotStaff } from './lib/ui';
import { loadConfig } from './config';
import { Dashboard } from './screens/Dashboard';
import { Families } from './screens/Families';
import { Users } from './screens/Users';
import { Asset, Clips } from './screens/Clips';
import { Videos } from './screens/Videos';
import { Ledger } from './screens/Ledger';
import { Pipeline } from './screens/Pipeline';
import { Executions } from './screens/Executions';
import { Commission } from './screens/Commission';
import { Characters } from './screens/Characters';
import { Verdicts } from './screens/Verdicts';
import { Devices } from './screens/Devices';
import { Config } from './screens/Config';
import { Health } from './screens/Health';
import { go, href, useRoute, type Route, type TabName } from './lib/route';


/**
 * The header, in the order an operator needs things.
 *
 * `TabName` rather than `Route['name']`: it excludes `commission`, which is a
 * detail route with a REQUIRED id and therefore has no bare tab to link to.
 * Typing it that way is what stops someone adding a `/commissions` tab that
 * builds fine and navigates to a page that cannot render.
 *
 * Ordered as the two questions this console exists to answer — "what is running
 * right now, and for whom", then "what did we generate, and was it alright" —
 * followed by the entity browsers behind them.
 */
const TABS: Array<[TabName, string]> = [
  ['dashboard', 'Overview'],
  ['pipeline', 'Pipeline'],
  ['executions', 'Executions'],
  ['videos', 'Videos'],
  ['clips', 'Clips'],
  ['characters', 'Characters'],
  ['families', 'Families'],
  ['users', 'Users'],
  ['devices', 'Devices'],
  ['verdicts', 'Safety'],
  ['ledger', 'Money'],
  ['config', 'Config'],
  ['health', 'Health'],
];

/**
 * Where the console can be, before it is a console.
 *
 * Five of these six are refusals, and they are separate states because they
 * have five different fixes. Collapsing any two of them puts an operator on the
 * wrong errand: `refused` and `not-staff` are the same missing role assignment
 * seen from either side of the API; `unwired` is a deployment that never ran
 * auth_setup.sh; `error` is this SPA's own config; `signed-out` is just a
 * button. The one thing none of them is allowed to be is a console that renders
 * and shows nothing.
 */
type Boot =
  | { state: 'loading' }
  | { state: 'error'; message: string }
  | { state: 'signed-out' }
  | { state: 'refused'; message: string; unassigned: boolean }
  | { state: 'not-staff'; message: string }
  | { state: 'unwired'; message: string }
  | { state: 'ready'; email: string };

/**
 * The staff control plane.
 *
 * Signs in against the showling-staff app registration and talks to the staff
 * surface under /control. One Entra tenant serves this and the parent portal,
 * so what keeps the two apart is the audience claim plus the `staff` app role —
 * see auth.ts. Nothing here can reach a family route.
 */
export function App() {
  const [boot, setBoot] = useState<Boot>({ state: 'loading' });
  // Location lives in the URL, not in state, so every view is a link an admin
  // can paste to another admin — and a refresh keeps you where you were.
  const route = useRoute();

  useEffect(() => {
    // ORDER MATTERS AND IT IS ENFORCED BY awaits. configureAuth() does not
    // return until MSAL has initialized AND handleRedirectPromise() has run;
    // asking for accounts before that is the classic redirect-flow bug, and it
    // presents as a signed-in operator being bounced to sign-in on every single
    // page load with nothing in the console to explain it.
    loadConfig()
      .then(async (config): Promise<Boot> => {
        const outcome = await configureAuth(config);
        configureApi(config);
        if (outcome.kind === 'refused') {
          return { state: 'refused', message: outcome.message, unassigned: outcome.unassigned };
        }
        const email = currentUserEmail();
        if (!email) return { state: 'signed-out' };
        return probe(email);
      })
      .then(setBoot)
      .catch((err: unknown) => setBoot({ state: 'error', message: message(err) }));
  }, []);

  const signOut = useCallback(async () => {
    await endSession();
  }, []);

  if (boot.state === 'loading') return <div className="centre">loading…</div>;
  if (boot.state === 'error') {
    return (
      <div className="centre">
        <div className="panel">
          <h1>Cannot start</h1>
          <p className="err">{boot.message}</p>
        </div>
      </div>
    );
  }
  if (boot.state === 'signed-out') return <SignIn />;
  if (boot.state === 'refused') return <Refused boot={boot} onSignOut={signOut} />;
  if (boot.state === 'not-staff') {
    return (
      <div className="centre">
        <div className="panel">
          <h1>
            showling <b>admin</b>
          </h1>
          <NotStaff reason={boot.message} />
          <button onClick={signOut}>Sign out and use another account</button>
        </div>
      </div>
    );
  }
  if (boot.state === 'unwired') {
    return (
      <div className="centre">
        <div className="panel">
          <h1>The staff API has no identity provider</h1>
          <p className="err">{boot.message}</p>
          <p className="sub">
            Every staff route answers this, not just the one this page tried, so
            the console is shown as one message rather than thirteen. The fix is{' '}
            <span className="mono">azure/auth_setup.sh</span>, which sets
            STAFF_ISSUER, STAFF_AUDIENCE and STAFF_ROLE on the Function App.
          </p>
          <button onClick={signOut}>Sign out</button>
        </div>
      </div>
    );
  }

  const email = boot.email;

  return (
    <div className="shell">
      <header>
        <span className="brand">
          showling <b>admin</b>
        </span>
        <nav>
          {TABS.map(([key, label]) => (
            // A real <a> with a real href: middle-click, cmd-click and
            // "copy link address" all work, which is the whole point of
            // making these addressable. The click handler only stops the
            // full page reload.
            <a
              key={key}
              href={href({ name: key } as Route)}
              className={route.name === key ? 'on' : ''}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                go({ name: key } as Route);
              }}
            >
              {label}
            </a>
          ))}
        </nav>
        <span className="who">
          {email}
          <button className="link" onClick={signOut}>
            sign out
          </button>
        </span>
      </header>
      <main>
        <Screen route={route} />
      </main>
    </div>
  );
}

/**
 * One call to /admin/health, to find out whether this account can use the
 * console at all.
 *
 * WHY A PROBE AND NOT A LATCH ON THE FIRST 403. The role refusal is a
 * whole-application fact, and without asking for it once at boot it arrives
 * thirteen times, as a red retryable box on whichever screen the operator
 * happened to open — which reads as "the console is broken" rather than "you
 * are not staff", and sends them to the wrong place. /admin/health is the right
 * question to ask: it reads no table and touches no seam, so it can only fail
 * on identity.
 *
 * ANYTHING ELSE LETS THE CONSOLE OPEN. A probe that blocked on a network blip
 * would take the whole console away at exactly the moment it is needed, and
 * every screen already reports its own failure honestly. Only the two answers
 * that are definitely true of every route stop the boot.
 */
async function probe(email: string): Promise<Boot> {
  try {
    await getHealth();
  } catch (err: unknown) {
    if (isNotStaff(err)) return { state: 'not-staff', message: message(err) };
    // 503 here means auth.py has no staff issuer/audience/role configured, so
    // it is refusing before any handler runs. /admin/health has no other way to
    // be unavailable, which is what makes this inference safe.
    if (isUnavailable(err)) return { state: 'unwired', message: message(err) };
  }
  return { state: 'ready', email };
}

/**
 * Entra itself turned this person away, before any token existed.
 *
 * Distinct from `not-staff` only in who said no. entra_bootstrap.sh sets
 * appRoleAssignmentRequired on the staff service principal, so an unassigned
 * account never gets a staff token at all — the refusal happens at sign-in and
 * arrives here as an AADSTS code instead of an HTTP status.
 */
function Refused({
  boot,
  onSignOut,
}: {
  boot: { message: string; unassigned: boolean };
  onSignOut: () => void;
}) {
  return (
    <div className="centre">
      <div className="panel">
        <h1>
          showling <b>admin</b>
        </h1>
        <div className="notice notice-bad">
          <b>
            {boot.unassigned
              ? 'Entra would not issue a token for this account.'
              : 'Sign-in did not complete.'}
          </b>
          <span className="mono small">{boot.message}</span>
          {boot.unassigned && (
            <span className="muted small">
              AADSTS50105 — the account is not assigned to showling-staff.
              Assignment is required on that application on purpose, so that an
              unassigned user never holds a staff token at all. Assign the{' '}
              <span className="mono">staff</span> app role and sign in again.
            </span>
          )}
        </div>
        <button onClick={onSignOut}>Sign out and use another account</button>
      </div>
    </div>
  );
}

/**
 * One route, one screen.
 *
 * A `switch` with an exhaustiveness check rather than the chain of
 * `{route.name === 'x' && <X/>}` this replaced. That chain was not a style
 * problem: `route.ts` defined fourteen routes and the chain rendered six, so
 * every link to a commission, an execution, a character or the config landed on
 * a page that rendered NOTHING — no error, no empty state, just a blank panel
 * under a working header. The Overview links to three of them.
 *
 * With a switch the compiler is what enforces coverage. Adding a route to
 * `Route` without adding it here fails `tsc` on the `never` assignment below,
 * which is the only mechanism that actually survives someone being in a hurry.
 */
function Screen({ route }: { route: Route }) {
  switch (route.name) {
    case 'dashboard':
      return <Dashboard />;
    case 'pipeline':
      return (
        <Pipeline
          initialStatus={route.status}
          initialFamily={route.familyId}
          initialKind={route.kind}
        />
      );
    case 'executions':
      return <Executions initialStatus={route.status} />;
    case 'commission':
      return <Commission commissionId={route.commissionId} />;
    case 'families':
      return <Families openId={route.familyId} />;
    case 'users':
      return <Users initialFilter={route.filter} />;
    case 'videos':
      return <Videos initialFamily={route.familyId} />;
    case 'clips':
      // /clips/<assetId> is the detail view of one pooled asset; the bare path
      // is the browser. Same route name because they are the same noun, and the
      // id in the path is what tells them apart.
      return route.assetId ? (
        <Asset assetId={route.assetId} />
      ) : (
        <Clips initialKind={route.kind} initialFamily={route.familyId} />
      );
    case 'characters':
      return <Characters initialFamily={route.familyId} />;
    case 'verdicts':
      return <Verdicts initialAllowed={route.allowed} />;
    case 'devices':
      return <Devices initialFamily={route.familyId} />;
    case 'ledger':
      return <Ledger />;
    case 'config':
      return <Config />;
    case 'health':
      return <Health />;
    default: {
      // If this stops compiling, a route was added to `Route` and never given a
      // screen. That is the bug this whole function exists to make impossible.
      const unreachable: never = route;
      return <>{String(unreachable)}</>;
    }
  }
}

/**
 * One button, and that is the entire screen now.
 *
 * There used to be an email field, a password field and a whole second state
 * for Cognito's FORCE_CHANGE_PASSWORD challenge — every staff account was
 * created with an admin temporary password, so answering that challenge was the
 * normal first sign-in. Entra owns first sign-in, password change, MFA and
 * recovery on its own hosted pages, so there is no challenge left to render and
 * no credential this SPA should ever be typed into.
 */
function SignIn() {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      // Navigates away. Anything after this only runs if the redirect never
      // happened, which is itself the error worth showing.
      await startSignIn();
    } catch (err: unknown) {
      setError(message(err));
      setBusy(false);
    }
  };

  return (
    <div className="centre">
      <form className="panel" onSubmit={submit}>
        <h1>
          showling <b>admin</b>
        </h1>
        <p className="sub">Staff sign-in. This is not the parent portal.</p>
        {error && <p className="err">{error}</p>}
        <button className="primary" disabled={busy}>
          {busy ? '…' : 'Sign in with Microsoft'}
        </button>
      </form>
    </div>
  );
}
